import React, { useState } from "react";
import { ApolloError, gql, useLazyQuery, useMutation } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";

import { Button } from "src/components/button/button";
import { Input } from "src/components/input/input";
import { Modal } from "src/components/modal/modal";
import { Body } from "src/components/typography/typography";
import { CloudCodeFunction, Report } from "src/generated/types";
import { parseError } from "src/utils/parse_error";
import { Routes } from "../../../utils/routes";

import { AdministratorsHelp, SupportStaffHelp } from "./role_help";

/*
 * GraphQL
 */

export const UPDATE_CONSTITUENT_GROUPS_MUTATION = gql`
    mutation UpdateConstituentGroups($input: CallCloudCodeInput!) {
        callCloudCode(input: $input) {
            result
        }
    }
`;

const CURRENT_REPORT_QUERY = gql`
    query DashboardData {
        viewer {
            user {
                id
                school {
                    id
                    currentReport {
                        id
                        promotionProgress
                        percentCompleted
                        status
                        submissionProgress
                        surveysCompleted
                        constituentGroups
                    }
                    currentReports {
                        edges {
                            node {
                                id
                                type
                                promotionProgress
                                percentCompleted
                                status
                                submissionProgress
                                surveysCompleted
                                constituentGroups
                            }
                        }
                    }
                }
            }
        }
    }
`;

/*
 * Types
 */

interface ConstituentGroupsFormProps {
    report: Report;
    schoolId?: string;
}

interface FormValues {
    administrators: string;
    alumni: string;
    boardMembers: string;
    parents: string;
    students: string;
    supportStaff: string;
    teachers: string;
}

/*
 * Main
 */

export const ConstituentGroupsForm = (props: ConstituentGroupsFormProps): React.ReactElement => {
    const { report, schoolId } = props;
    const type = report.type;
    const history = useHistory();

    const [isAdministratorsModalOpen, setAdministratorsModalOpen] = useState(false);
    const [isAlumniModalOpen, setAlumniModalOpen] = useState(false);
    const [isBoardMembersModalOpen, setBoardMembersModalOpen] = useState(false);
    const [isParentsModalOpen, setParentsModalOpen] = useState(false);
    const [isStudentModalOpen, setStudentModalOpen] = useState(false);
    const [isSupportStaffModalOpen, setSupportStaffModalOpen] = useState(false);
    const [isTeachersModalOpen, setTeachersModalOpen] = useState(false);
    const [updateConstituentGroups] = useMutation(UPDATE_CONSTITUENT_GROUPS_MUTATION);
    const [refreshReport] = useLazyQuery(CURRENT_REPORT_QUERY, {
        fetchPolicy: "network-only",
    });

    const initialValues = {
        administrators: report.constituentGroups.administrators ?? 0,
        alumni: report.constituentGroups.alumni ?? 0,
        boardMembers: report.constituentGroups.boardMembers ?? 0,
        parents: report.constituentGroups.parents ?? 0,
        students: report.constituentGroups.students ?? 0,
        supportStaff: report.constituentGroups.supportStaff ?? 0,
        teachers: report.constituentGroups.teachers ?? 0,
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, { setSubmitting }): void => {
                updateConstituentGroups({
                    variables: {
                        input: {
                            functionName: CloudCodeFunction.UpdateConstituentGroupsForCurrentReports,
                            params: {
                                schoolId,
                                constituentGroups: {
                                    [type]: {
                                        administrators: parseInt(values.administrators, 10),
                                        alumni: parseInt(values.alumni, 10),
                                        boardMembers: parseInt(values.boardMembers, 10),
                                        parents: parseInt(values.parents, 10),
                                        students: parseInt(values.students, 10),
                                        supportStaff: parseInt(values.supportStaff, 10),
                                        teachers: parseInt(values.teachers, 10),
                                    },
                                },
                            },
                        },
                    },
                })
                    .then(() => {
                        refreshReport()
                            .then(() => {
                                history.push(Routes.promotion);
                                toast.success("Constituent groups successfully updated.");
                            })
                            .catch((error: ApolloError) => {
                                toast.error(parseError(error));
                            });
                    })
                    .catch((error: ApolloError) => {
                        toast.error(parseError(error));
                    });

                setSubmitting(false);
            }}
            validationSchema={Yup.object().shape({
                administrators: Yup.string().required("Required"),
                alumni: Yup.string().required("Required"),
                boardMembers: Yup.string().required("Required"),
                parents: Yup.string().required("Required"),
                students: Yup.string().required("Required"),
                supportStaff: Yup.string().required("Required"),
                teachers: Yup.string().required("Required"),
            })}
        >
            {(formik: FormikProps<FormValues>) => (
                <Form>
                    <div className="form-field-column-wrap">
                        <div className="form-field-column col1">
                            <Input
                                id="students"
                                label="Students"
                                name="students"
                                placeholder="0"
                                renderLabelMeta={() => {
                                    return (
                                        <div className="modal-link-container">
                                            <span
                                                className="whats-this-text"
                                                onClick={(): void => {
                                                    setStudentModalOpen(true);
                                                }}
                                            >
                                                What's this?
                                            </span>
                                            <Modal
                                                isOpen={isStudentModalOpen}
                                                setOpen={setStudentModalOpen}
                                                title="Students"
                                            >
                                                <Body>
                                                    {type === "ffi" ? (
                                                        <>
                                                            Enter the TOTAL number of students enrolled in grade 8 or
                                                            higher. This includes all relevant full-time, part-time, or
                                                            distance students. If your school has only students up
                                                            through 7th grade, enter ZERO (0).
                                                        </>
                                                    ) : (
                                                        <>
                                                            Enter the TOTAL number of students enrolled in grade 6 or
                                                            higher. This includes all relevant full-time, part-time, or
                                                            distance students. If your school has only students up
                                                            through 5th grade, enter ZERO (0).
                                                        </>
                                                    )}
                                                </Body>
                                            </Modal>
                                        </div>
                                    );
                                }}
                                type="text"
                            />
                            <Input
                                id="administrators"
                                label="Administrators"
                                name="administrators"
                                placeholder="0"
                                renderLabelMeta={() => {
                                    return (
                                        <div className="modal-link-container">
                                            <span
                                                className="whats-this-text"
                                                onClick={(): void => {
                                                    setAdministratorsModalOpen(true);
                                                }}
                                            >
                                                What's this?
                                            </span>
                                            <Modal
                                                isOpen={isAdministratorsModalOpen}
                                                setOpen={setAdministratorsModalOpen}
                                                title="Administrators"
                                            >
                                                <AdministratorsHelp />
                                            </Modal>
                                        </div>
                                    );
                                }}
                                type="text"
                            />
                            <Input
                                id="parents"
                                label="Parents"
                                name="parents"
                                placeholder="0"
                                renderLabelMeta={() => {
                                    return (
                                        <div className="modal-link-container">
                                            <span
                                                className="whats-this-text"
                                                onClick={(): void => {
                                                    setParentsModalOpen(true);
                                                }}
                                            >
                                                What's this?
                                            </span>
                                            <Modal
                                                isOpen={isParentsModalOpen}
                                                setOpen={setParentsModalOpen}
                                                title="Parents"
                                            >
                                                <Body>
                                                    Enter the TOTAL number of parents and legal guardians. If
                                                    distributing primarily or exclusively via email, enter the total
                                                    number of valid, current parent/guardian email addresses you will be
                                                    contacting (size of your parent contact list).
                                                </Body>
                                            </Modal>
                                        </div>
                                    );
                                }}
                                type="text"
                            />
                            <Input
                                id="boardMembers"
                                label="Board Members"
                                name="boardMembers"
                                placeholder="0"
                                renderLabelMeta={() => {
                                    return (
                                        <div className="modal-link-container">
                                            <span
                                                className="whats-this-text"
                                                onClick={(): void => {
                                                    setBoardMembersModalOpen(true);
                                                }}
                                            >
                                                What's this?
                                            </span>
                                            <Modal
                                                isOpen={isBoardMembersModalOpen}
                                                setOpen={setBoardMembersModalOpen}
                                                title="Board Members"
                                            >
                                                <Body>
                                                    Enter the TOTAL number of currently active board members for the
                                                    school. If your school has no board of directors or board members,
                                                    enter ZERO (0).
                                                </Body>
                                            </Modal>
                                        </div>
                                    );
                                }}
                                type="text"
                            />
                        </div>
                        <div className="form-field-column col2">
                            <Input
                                id="teachers"
                                label="Teachers"
                                name="teachers"
                                placeholder="0"
                                renderLabelMeta={() => {
                                    return (
                                        <div className="modal-link-container">
                                            <span
                                                className="whats-this-text"
                                                onClick={(): void => {
                                                    setTeachersModalOpen(true);
                                                }}
                                            >
                                                What's this?
                                            </span>
                                            <Modal
                                                isOpen={isTeachersModalOpen}
                                                setOpen={setTeachersModalOpen}
                                                title="Teachers"
                                            >
                                                <Body>
                                                    As teachers have a unique view across internal and family
                                                    stakeholders engaged with the school, the teacher questionnaire is
                                                    slightly longer than questionnaires presented to other groups.
                                                </Body>
                                                <Body>
                                                    The median time to complete the FSCI teacher questionnaire is 7
                                                    minutes and 40 seconds.
                                                </Body>
                                            </Modal>
                                        </div>
                                    );
                                }}
                                type="text"
                            />
                            <Input
                                id="supportStaff"
                                label="Support Staff"
                                name="supportStaff"
                                placeholder="0"
                                renderLabelMeta={() => {
                                    return (
                                        <div className="modal-link-container">
                                            <span
                                                className="whats-this-text"
                                                onClick={(): void => {
                                                    setSupportStaffModalOpen(true);
                                                }}
                                            >
                                                What's this?
                                            </span>
                                            <Modal
                                                isOpen={isSupportStaffModalOpen}
                                                setOpen={setSupportStaffModalOpen}
                                                title="Support Staff"
                                            >
                                                <SupportStaffHelp />
                                            </Modal>
                                        </div>
                                    );
                                }}
                                type="text"
                            />
                            <Input
                                id="alumni"
                                label="Alumni"
                                name="alumni"
                                placeholder="0"
                                renderLabelMeta={() => {
                                    return (
                                        <div className="modal-link-container">
                                            <span
                                                className="whats-this-text"
                                                onClick={(): void => {
                                                    setAlumniModalOpen(true);
                                                }}
                                            >
                                                What's this?
                                            </span>
                                            <Modal
                                                isOpen={isAlumniModalOpen}
                                                setOpen={setAlumniModalOpen}
                                                title="Alumni"
                                            >
                                                <Body>
                                                    Enter the TOTAL number of alumni of the school with whom you have
                                                    the ability to contact.
                                                </Body>
                                                <Body>
                                                    If distributing primarily or exclusively via email, enter the total
                                                    number of valid alumni email addresses you will be contacting. If
                                                    distributing primarily through social media channels (e.g.
                                                    Facebook), estimate the rough number of your active followers who
                                                    are alumni. If sending alumni links via multiple channels, add the
                                                    total numbers and estimates across all channels being used.
                                                </Body>
                                            </Modal>
                                        </div>
                                    );
                                }}
                                type="text"
                            />
                        </div>
                    </div>
                    <div className="constituent-submit-button-wrap">
                        <Button disabled={formik.isSubmitting || !formik.isValid} type="submit">
                            Submit Constituent Groups
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};
